import { CustomEmailConfigDTO } from './custom-email';
import { CustomPdfConfigDTO } from './custom-pdf';
import { EventCharge } from './event-charge';
// eslint-disable-next-line import/no-cycle
import { TicketSummaryDTO } from './order';
import { PaymentMethod } from './payment';
import { ShowtimeDTO } from './showtime';
import TicketDTO from './ticket';
import VenueDTO from './venue';
import { WireTransferDTO } from './wire-transfer';
import { ListDTO } from './list';

export default interface EventDTO {
  id: string,
  business_id: string,
  name: string,
  description: string,
  date: string,
  ends_at: string,
  place: string,
  active: boolean,
  terms: string,
  phone: string,
  tickets: TicketDTO[]
  banner_image: string;
  certificate_url?: string | null;
  maps_link: string;
  allow_mercadopago_payments: boolean;
  allow_payway_payments: boolean;
  card_promotion_ids: string[];
  installment_interest_rates?: Record<string, number> | null;
  payway_promotions_only?: boolean;
  allow_uala_payments: boolean;
  allow_nave_payments: boolean;
  allow_payway_link_payments: boolean;
  allow_zilo_payments: boolean;
  allow_stripe_payments: boolean;
  allow_fiserv_payments: boolean;
  allow_manual_payments: boolean;
  /** ISO 4217 code (default 'ARS'). Non-ARS events can only sell online through Stripe. */
  currency: string;
  allow_in_person_sales: boolean;
  online_service_charge: number;
  manual_service_charge: number;
  min_age: number;
  address: string;
  require_id_tickets: boolean;
  receives_wire_transfers: boolean;
  has_polls: boolean;
  is_registration_event: boolean;
  name_label: string | null;
  last_name_label: string | null;
  person_id_label: string | null;
  is_public: boolean;
  has_numerated_seats: boolean;
  tickets_includes_service_charge: boolean;
  has_seller_qr: boolean;
  has_scanner_auto_accept: boolean;
  /** The scanner may search tickets by the holder's DNI. */
  allow_scanner_person_id_search: boolean;
  /** Escáner v2: reading one ticket offers the rest of its order. */
  allow_order_entry?: boolean;
  has_order_qrs: boolean;
  /** Short sequential ids: "1042" for the order, "1042-150" for each ticket. */
  has_sequential_ids: boolean;
  has_coupons?: boolean;
  is_important: boolean;
  /** Available code balance for the event. */
  codes_available: number | null;
  /** Used when `is_important` is true; higher values typically sort first among featured events. */
  priority?: number;
  /** ROOT-only. Max paid items per person (same email or DNI) for the whole event; 0 = no limit. */
  max_items_per_person?: number;
  /** ROOT-only. Enables general-admission ticket holds (oversell block) during checkout. Default false. */
  has_ticket_reservations?: boolean;
  is_approved: boolean;
  public_display: boolean;
  banner_display: boolean;
  /** When true, the event is shown on its business landing page even if it is not publicly listed elsewhere (`public_display = false`). */
  show_on_business_landing?: boolean;
  /** Apertura de la venta pública (root-only). En el futuro, la página del evento muestra "Próximamente". */
  sales_start_at?: string | null;
  public_status: EventPublicStatus;
  /** Whether buyers can self-request refunds from the public refunds page. */
  public_refund_status?: EventPublicRefundStatus;
  seller_sales_enabled: boolean;
  slug: string;
  seller_can_download_tickets: boolean;
  venue?: VenueDTO;
  venue_id?: string | null;
  custom_ticket_label: string | null;
  /** Free-text note surfaced in the QR confirmation email ("Información importante"). ROOT-only. */
  custom_email_note: string | null;
  /** URL (or `;`-separated URLs) of extra PDF(s) attached to the QR confirmation email. ROOT-only. */
  extra_attachment_pdf: string | null;
  /** Per-section overrides for the QR confirmation email (jsonb). */
  custom_email_config?: CustomEmailConfigDTO | null;
  /** Per-section overrides for the ticket PDF (jsonb). */
  pdf_template_config?: CustomPdfConfigDTO | null;
  created_at: string;
  updated_at: string;
  has_audience_discounts: boolean;
  has_open_price: boolean;
  has_simplified_selling: boolean;
  has_showtimes: boolean;
  // Minutos que dura la reserva de entradas o butacas.
  reservation_duration?: number;
  showtimes?: ShowtimeDTO[];
  has_subscription: boolean;
  prints_subscriptions: boolean;
  notify_purchases: boolean;
  is_sold_out: boolean;
  business_name: string;
  superfast_checkout: boolean;
  has_order_referrals: 'enabled' | 'disabled' | 'enabled_unaccepted';
  meta_pixel_id: string | null;
  show_sellers_ranking: boolean;
  absorbed_service_charge_rate: number;
  online_comission_rate: number;
  has_lists: boolean;
  list?: ListDTO;
  has_attendance: boolean;
  order_phone_number: boolean;
  coproducer_reports: boolean;
  coproducer_balance: boolean;
  can_print_tickets: boolean;
  // Comunicaciones — root-only feature flag that shows/hides the section.
  has_communications?: boolean;
  // Comunicaciones — reminder config (managed via the reminders endpoints).
  reminders_enabled?: boolean;
  reminder_day_before?: boolean;
  reminder_hours_before?: boolean;
  reminder_manual_sent_at?: string | null;
  // Structured location columns served by the BE. Optional to allow the FE
  // to render legacy events that haven't been backfilled yet.
  city?: string;
  city_slug?: string;
  province?: string;
  province_slug?: string;
  country_code?: string;
  iso_3166_2?: string;
  /**
   * Public taxonomy categories assigned to the event, in assignment order.
   * Served by `GET /events/all/available` and `GET /events/slug/:slug`; other
   * endpoints omit the key. UI surfaces badge the first entry when present.
   */
  categories?: Array<{ slug: string; name: string }>;
}

export interface CategoryDTO {
  id: string;
  name: string;
  code: string;
  description: string | null;
  active: boolean;
  created_at: string;
  updated_at: string;
  /**
   * URL-safe stable slug used by SEO surfaces (`/categorias/:slug`).
   * Optional for back-compat with the legacy `/categories` endpoint;
   * the new public taxonomy endpoint is expected to populate it.
   */
  slug?: string;
}

/**
 * Province served from `GET /public/provinces`.
 * See backend Swagger spec under `Public Discovery`.
 */
export interface ProvinceDTO {
  slug: string;
  name: string;
  iso_3166_2: string;
  event_count: number;
}

/**
 * City served from `GET /public/cities/:province_slug`. Scoped to the province
 * in the URL, so the response does not echo `province_slug`.
 */
export interface CityDTO {
  slug: string;
  name: string;
  event_count: number;
}

/**
 * Category served from `GET /public/categories`.
 */
export interface PublicCategoryDTO {
  slug: string;
  name: string;
  event_count: number;
}

/**
 * Producer served from `GET /public/producers`.
 */
export interface PublicProducerDTO {
  slug: string;
  name: string;
  event_count: number;
}
export interface EventCategoryDTO {
  id: string;
  event_id: string;
  category_id: string;
  created_at: string;
  updated_at: string;
  event: EventDTO;
  category: CategoryDTO;
}

export interface SellerEventQR {
  qr_code: string;
  qr_image_b64: string;
}

export interface CouponSummaryDTO {
  coupon_id: string;
  code: string;
  type: 'percentage' | 'amount';
  value: number;
  limit_amount: number;
  taken_amount: number;
  active: boolean;
  orders_count: number;
  tickets_count: number;
  discounts_amount: number;
}

export interface EventSalesReportDTO {
  total_amount_manual: number;
  total_amount_online: number;
  total_amount: number;
  tickets_count_manual: number;
  tickets_count_online: number;
  total_tickets_count: number;
  ticket_summary: TicketSummaryDTO[];
  coupon_summary: CouponSummaryDTO[];
  total_manual_service_charge: number;
  total_amount_manual_with_service_charge: number;
  absorbed_service_charge_amount: number;
  sellers_manual_comission_amount: number;
  sellers_referral_comission_amount: number;
  total_comission_amount: number;
  net_amount: number;
  total_discounts: number;
  discounts_count: number;
  order_referral_comission_amount: number;
  sales_channel_share: {
    online: number;
    referral: number;
    seller: number;
  };
  comissions_share: {
    avantti: number;
    referral: number;
    seller: number;
    order_referral: number;
    absorbed_service_charge: number;
  };
}

export interface EventSalesByUserDTO {
  user_id: string;
  username: string;
  items_sold: number;
}

export interface EventAssistanceReportDTO {
  average_order_amount: number;
  average_tickets_per_order: number;
  tickets_count: number;
  tickets_sold_count: number;
  free_tickets_count: number;
  total_tickets_scanned: number;
  asistance_share:{
    assited: number;
    not_assited: number;
  };
  sales_channel_share: {
    online: number;
    manual: number;
    in_person: number;
  };
  free_tickets_share: {
    free: number;
    not_free: number;
  };
  sold_tickets_share: {
    not_available: number;
    available: number;
  };
  total_tickets_taken_by_type: {
    ticket_id: string;
    ticket_name: string;
    amount_taken: number;
  }[];
  total_tickets_taken_by_channel: {
    avantti: number;
    referrals: number;
    seller: number;
  };
  non_attendance_rate_by_ticket: {
    ticket_id: string;
    ticket_name: string;
    rate: number;
  }[];
  sales_in_period: Record<string, number>;
  sales_by_hour: Record<string, number>;
  tickets_used_by_hour: Record<string, number>;
  sales_until_event_end: Record<string, number>;
}

export interface SellerReferralsReportDTO {
  total_referral_amount: number;
  total_seller_amount: number;
  total_comission_amount: number;
  net_seller_amount: number;
  best_sellers: {
    username: string;
    total_amount: number;
  }[];
  order_average_best_sellers: {
    username: string;
    average_order_amount: number;
  }[];
  best_referrals: {
    referral_code: string;
    total_amount: number;
  }[];
}

export interface EventBalanceDTO {
  total_processed: number;
  tickets_amount: number;
  total_discounts: number;
  net_total: number;
  avantti_revenue: number;
  avantti_manual_fees: number;
  avantti_online_fees: number;
  gateway_fees: number;
  total_in_business_accounts: number;
  total_transferred: number;
  total_balance: number;
  wires: WireTransferDTO[];
  avantti_gross_amount_online: number;
  gross_received_manual_amount: number;
  absorbed_service_charge_amount: number;
  total_charges: number; // net signed sum of charge balance_impacts (already in total_balance)
  charges: EventCharge[];
}

export interface EventPendingBalanceDTO {
  event_id: string;
  event_name: string;
  pending_balance: number;
  ends_at: string;
}

export interface AllEventsPendingBalanceDTO {
  pending_balance: number;
  events: EventPendingBalanceDTO[];
}

// Snapshot de plata de productoras en cuentas Avantti a un instante dado
// (GET /events/all/pending-balance-at). Solo considera eventos desde 2026.
export interface PendingBalanceAtDTO {
  at: string;
  pending_balance: number;
}

export enum EventPublicStatus {
  Ready = 'ready',
  PendingApproval = 'pending_approval',
  NoTicketsAvailable = 'no_tickets_available',
  Expired = 'expired',
  Inactive = 'inactive',
}

export enum EventPublicRefundStatus {
  None = 'none',
  OnlyTickets = 'only_tickets',
  Full = 'full',
}

export interface BasicEventDTO {
  id: string;
  business_id: string;
  business_name: string | null;
  name: string;
  slug: string;
  date: string;
  ends_at: string;
  active: boolean;
  is_public: boolean;
  is_approved: boolean;
  banner_image: string;
  place: string;
  allow_in_person_sales: boolean;
  public_display: boolean;
  public_status: EventPublicStatus;
}

export interface BasicEventsQueryParams {
  active?: boolean;
  is_public?: boolean;
  business_id?: string;
}

export interface EventAudienceDiscountDTO {
  id: string;
  name: string;
  requirements: string;
  percentage: number;
  active: boolean;
}

export interface CreateEventAudienceDiscountDTO {
  name: string;
  requirements: string;
  percentage: number;
}

export interface UpdateEventAudienceDiscountDTO {
  id: string;
  event_id: string;
  name: string;
  requirements: string;
  percentage: number;
}

export interface EventSubscriptionDTO {
  id: string;
  subscription_event_id: string;
  subscribed_event_id: string;
}

export interface CreateEventSubscriptionDTO {
  subscribed_event_id: string;
}

export interface EventSummaryDTO {
  event_id: string;
  event_name: string;
  start_date: Date;
  end_date: Date;
  business_name: string;
  status: EventPublicStatus;
  total_money_processed: number;
  money_in_our_hands: number;
  money_in_their_hands: number;
  mp_fees: number;
  online_service_charge_percentage: number;
  manual_service_charge_percentage: number;
  total_service_charge: number;
  online_service_charge_total: number;
  manual_service_charge_total: number;
  liquidation_amount: number;
  payment_method: Record<PaymentMethod, number>;
  total_tickets_sold: number;
  last_sell_date: Date | null;
}

export interface EventStatsDTO {
  total_sales_amount: string;
  total_orders: string;
  total_tickets_sold: string;
  events: EventSummaryDTO[];
}

export interface PlatformSalesChartPoint {
  date: string;
  revenue_amount: number;
  tickets_count: number;
}

export interface PlatformSalesChartDTO {
  start: string;
  end: string;
  data: PlatformSalesChartPoint[];
  totals: {
    revenue_amount: number;
    tickets_count: number;
  };
}

export interface PlatformChartsDayPoint {
  date: string;
  events_created: number;
  events_created_cumulative: number;
  tickets_sold: number;
  tickets_sold_cumulative: number;
  businesses_created: number;
  businesses_created_cumulative: number;
  processed_amount: number;
  processed_amount_cumulative: number;
  monetized_tickets: number;
  monetized_tickets_cumulative: number;
  not_monetized_tickets: number;
  not_monetized_tickets_cumulative: number;
  manual_tickets: number;
  manual_tickets_cumulative: number;
  online_tickets: number;
  online_tickets_cumulative: number;
  service_charge_amount: number;
  service_charge_amount_cumulative: number;
  active_events: number;
}

export interface PlatformChartsDTO {
  start: string;
  end: string;
  data: PlatformChartsDayPoint[];
}

export interface PlatformMetricsTotals {
  service_charge_total: number;
  tickets_sold: number;
  new_producers: number;
  processed_amount: number;
  monetized_tickets: number;
  not_monetized_tickets: number;
  manual_tickets: number;
  online_tickets: number;
  service_charge_charged: number;
  active_producers: number;
  service_charge_included: number;
  absorbed_service_charge: number;
  manual_commission: number;
  online_commission: number;
  producer_collected: number;
  events_with_payments: number;
  payment_tickets_count: number;
  non_printed_orders_count: number;
  non_printed_tickets_count: number;
}

export interface PlatformRangeChartsDTO {
  start: string;
  end: string;
  totals: PlatformMetricsTotals;
}

export interface PlatformMonthlyMetricsMonth extends PlatformMetricsTotals {
  month: string;
}

export interface PlatformMonthlyChartsDTO {
  start: string | null;
  end: string | null;
  months: PlatformMonthlyMetricsMonth[];
}

export interface TopGrossingProducer {
  business_id: string;
  business_name: string;
  /** Gross: sum of paid_amount on approved payments. */
  processed_amount: number;
  service_charge_total: number;
  /** Platform revenue: service charges + commissions + event charges. */
  total_revenue: number;
  producer_collected: number;
  tickets_sold: number;
  /** Distinct events with approved payments. */
  events_count: number;
}

export interface TopGrossingEvent {
  event_id: string;
  event_name: string;
  business_id: string;
  business_name: string;
  processed_amount: number;
  service_charge_total: number;
  /** Platform revenue: service charges + commissions + event charges. */
  total_revenue: number;
  producer_collected: number;
  tickets_sold: number;
}

export interface TopGrossingDTO {
  start: string;
  end: string;
  limit: number;
  /** Producers ranked by processed_amount DESC. */
  producers: TopGrossingProducer[];
  /** Events ranked by processed_amount DESC. */
  events: TopGrossingEvent[];
}
